'use client';
import Avo, { OverlayStepValueType } from '@susu/custom-made/Avo';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { OnBoardingItem } from '../onBoardingItem/onBoardingItem';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';

const { ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

export const OnBoardingPanel = ({
  title,
  ctaText,
  stepAmount,
  ctaClick,
  currentStep,
  setCurrentStep,
  overlayStep,
}: {
  title: string[];
  ctaText: string;
  stepAmount: number;
  ctaClick: () => void;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  overlayStep: (stepNo: number) => string;
}) => {
  const { section } = useNavigationContext();
  const { getAvoStandardEventParams } = useAvo();
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const [animationDone, setAnimationDone] = useState<boolean>(false);
  const firstItemInterval = useRef<NodeJS.Timeout>();
  const firstItemIntervalTime = 2000;
  const restItemsInterval = useRef<NodeJS.Timeout>();
  const restItemsIntervalTime = 6000;

  const callAvoOverlayShowed = (autoTriggered: boolean, stepNo: number) => {
    if (!isAvoTrackingEnabled) return;
    Avo.customMadeOverlayShowed({
      ...getAvoStandardEventParams(section!),
      overlayStep: overlayStep(stepNo) as OverlayStepValueType,
      autoTriggered: autoTriggered,
    });
  };

  const startFirstItemInterval = () => {
    firstItemInterval.current = setInterval(() => {
      setCurrentStep(1);
    }, firstItemIntervalTime);
  };

  const startRestItemsInterval = () => {
    restItemsInterval.current = setInterval(() => {
      setCurrentStep(step => step + 1);
    }, restItemsIntervalTime);
  };

  const changeStepClick = (step: number) => {
    setAnimationDone(true);
    setCurrentStep(step);
    clearInterval(firstItemInterval.current);
    clearInterval(restItemsInterval.current);
    callAvoOverlayShowed(false, step);
  };

  useEffect(() => {
    if (currentStep >= stepAmount - 1) {
      clearInterval(restItemsInterval.current);
      setTimeout(() => {
        setAnimationDone(true);
      }, restItemsIntervalTime);
    }

    if (currentStep === 0 && !firstItemInterval.current) {
      callAvoOverlayShowed(true, currentStep);
      startFirstItemInterval();
    }

    if (currentStep === 1 && firstItemInterval.current && !restItemsInterval.current) {
      callAvoOverlayShowed(true, currentStep);
      clearInterval(firstItemInterval.current);
      startRestItemsInterval();
    }
  }, [currentStep, setCurrentStep]);

  return (
    <OnBoardingItem
      titles={title}
      ctaText={ctaText}
      stepAmount={stepAmount}
      currentStep={currentStep}
      ctaClick={ctaClick}
      changeStepClick={changeStepClick}
      animationDone={animationDone}
    />
  );
};
